import React, { useEffect, useState, Suspense } from 'react';
import { Hero } from './components/Hero';
import { NeonLightning } from './components/NeonLightning';
import { DriftMarks } from './components/DriftMarks';
import { PilotosLogin } from './components/PilotosLogin';
import { ScrollPortal } from './components/ScrollPortal';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { getAssetUrl } from './lib/supabaseClient';

// Lazy load components below the fold
const HowItWorks = React.lazy(() => import('./components/HowItWorks').then(m => ({ default: m.HowItWorks })));
const BentoGrid = React.lazy(() => import('./components/BentoGrid').then(m => ({ default: m.BentoGrid })));
const Paddock = React.lazy(() => import('./components/Paddock').then(m => ({ default: m.Paddock })));
const Leaderboard = React.lazy(() => import('./components/Leaderboard').then(m => ({ default: m.Leaderboard })));
const MapHUD = React.lazy(() => import('./components/MapHUD').then(m => ({ default: m.MapHUD })));
const Footer = React.lazy(() => import('./components/Footer').then(m => ({ default: m.Footer })));

const App: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Scroll Progress
  const { scrollYProgress, scrollY } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    // Imágenes críticas que deben cargar antes de quitar el loader
    const criticalImages = [
      getAssetUrl('Image-Hero1.png'),
      getAssetUrl('Image-Hero2.png'),
      getAssetUrl('logo.png')
    ];

    // Promesa que se resuelve cuando todas las imágenes están cargadas (o fallan)
    const imagesPromise = Promise.all(
      criticalImages.map((src) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = src;
          img.onload = resolve;
          img.onerror = resolve; // Resolvemos igual en caso de error para no bloquear
        });
      })
    );

    // The portal owns its video: do not create a second decoder just for loading.
    // Never hold the page hostage to a slow image or a decorative minimum delay.
    let mounted = true;
    const timeout = window.setTimeout(() => { if (mounted) setIsLoading(false); }, 1500);
    imagesPromise.then(() => { if (mounted) { clearTimeout(timeout); setIsLoading(false); } });
    return () => { mounted = false; clearTimeout(timeout); };
  }, []);

  useEffect(() => {
    return scrollY.on("change", (latest) => {
      setShowBackToTop(latest > 500);
    });
  }, [scrollY]);

  useEffect(() => {
    const handleOpenBooking = () => {
      window.dispatchEvent(new CustomEvent('skip-intro'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setShowLogin(true);
    };
    window.addEventListener('open-booking', handleOpenBooking);
    return () => window.removeEventListener('open-booking', handleOpenBooking);
  }, []);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
  }, []);

  const handleInicioClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('skip-intro'));
    window.history.pushState(null, '', '#inicio');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const handleNavClick = () => {
    window.dispatchEvent(new CustomEvent('skip-intro'));
    setIsMenuOpen(false);
  };

  const navLinks = [
    { name: 'Inicio', href: '#inicio', onClick: handleInicioClick },
    { name: 'Cómo Funciona', href: '#como-funciona', onClick: handleNavClick },
    { name: 'Tecnología', href: '#tecnologia', onClick: handleNavClick },
    { name: 'Paddock', href: '#paddock', onClick: handleNavClick },
    { name: 'Ubicación', href: '#ubicacion', onClick: handleNavClick },
  ];

  return (
    <main className="relative min-h-screen bg-[#050505] overflow-x-hidden carbon-texture cursor-auto">
      <AnimatePresence>
        {isLoading && (
          <motion.div 
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="fixed inset-0 z-[99999] bg-[#050505] flex flex-col items-center justify-center"
          >
            <div className="relative w-24 h-24 mb-8">
              <div 
                className="absolute inset-0 border-t-2 border-cyan-400 rounded-full animate-[spin_2s_linear_infinite]"
              />
              <div 
                className="absolute inset-2 border-b-2 border-magenta-500 rounded-full opacity-50 animate-[spin_3s_linear_infinite_reverse]"
              />
              <img 
                src="/isotipo-simp.png" 
                alt="Loading" 
                className="absolute inset-0 m-auto w-10 h-10 object-contain animate-pulse"
              />
            </div>
            <div className="font-racing text-cyan-400 tracking-[0.5em] text-xs uppercase animate-pulse">
              Inicializando Sistema...
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      
      {/* Scroll Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-400 to-magenta-500 origin-left z-[200]"
        style={{ scaleX }}
      />

      {/* Global Visual Overlays */}
      <div className="hidden md:block fixed inset-0 pointer-events-none z-50 opacity-[0.025] bg-noise"></div>
      
      <div className="fixed inset-0 pointer-events-none z-[1]">
        <NeonLightning />
        <DriftMarks />
      </div>

      {/* Navigation Header */}
      <header className="fixed top-0 left-0 w-full z-[120] px-4 py-3 md:px-10 md:py-6 flex justify-between items-center backdrop-blur-xl bg-black/80 border-b border-white/5 gap-3">
        <div 
          className="flex items-center gap-4 cursor-pointer group"
          onClick={() => window.location.href = '/'}
        >
          {/* ISOTIPO NAVBAR */}
          <div className="relative w-12 h-12 flex items-center justify-center bg-white/5 rounded-lg border border-white/10">
            <motion.div 
              whileHover={{ rotate: 90, scale: 1.1 }}
              className="absolute inset-0 border border-cyan-400/20 group-hover:border-cyan-400/80 transition-all duration-700"
            />
            <img 
              src="/isotipo-simp.png" 
              alt="TurVolt Isotipo" 
              className="w-8 h-8 object-contain relative z-10 block"
            />
          </div>
          <div className="font-racing font-black text-xl md:text-2xl tracking-tighter uppercase leading-none hidden sm:block">
            TUR<span className="text-magenta-500 italic">VOLT</span>
            <span className="block text-[7px] tracking-[0.5em] text-cyan-400 font-bold opacity-50">Underground</span>
          </div>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex gap-6 lg:gap-10 font-racing text-[9px] uppercase tracking-[0.3em] lg:tracking-[0.4em] text-gray-500">
          {navLinks.map((link) => (
            <a 
              key={link.name} 
              href={link.href} 
              onClick={link.onClick}
              className="hover:text-white transition-all hover:tracking-[0.5em] relative py-2"
            >
              {link.name}
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-[2px] bg-cyan-400 transition-all hover:w-full"></span>
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-6">
          {/* NUEVO: Al hacer clic, activamos el estado showLogin */}
          <button 
            onClick={() => {
              window.dispatchEvent(new CustomEvent('skip-intro'));
              window.scrollTo({ top: 0, behavior: 'smooth' });
              setShowLogin(true);
            }}
            className="hidden lg:block border border-white text-white px-4 xl:px-6 py-2 text-[9px] font-racing font-bold uppercase hover:bg-white hover:text-black transition-colors shadow-[0_0_15px_rgba(255,255,255,0.15)] italic tracking-widest whitespace-nowrap"
          >
            Pilotos_Login
          </button>
          
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white flex flex-col gap-1.5 p-2 bg-white/5 rounded border border-white/10"
          >
            <div className={`w-6 h-0.5 bg-cyan-400 transition-all ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></div>
            <div className={`w-6 h-0.5 bg-white transition-all ${isMenuOpen ? 'opacity-0' : ''}`}></div>
            <div className={`w-6 h-0.5 bg-magenta-500 transition-all ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></div>
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            className="fixed inset-0 z-[110] bg-black/98 backdrop-blur-3xl flex flex-col items-center justify-center p-6 md:hidden"
          >
            <nav className="flex flex-col gap-6 text-center mt-8">
              {navLinks.map((link) => (
                <a 
                  key={link.name} 
                  href={link.href} 
                  onClick={link.onClick || (() => setIsMenuOpen(false))}
                  className="text-2xl sm:text-3xl font-racing font-black uppercase text-white hover:text-cyan-400 transition-colors italic tracking-widest"
                >
                  {link.name}
                </a>
              ))}
            </nav>
            {/* NUEVO: También conectamos el botón del menú móvil */}
            <button 
              onClick={() => {
                setIsMenuOpen(false);
                window.dispatchEvent(new CustomEvent('skip-intro'));
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setShowLogin(true);
              }}
              className="mt-12 bg-gradient-to-r from-cyan-500 to-magenta-600 text-white px-6 py-4 font-racing font-bold uppercase w-full max-w-xs tracking-[0.2em] text-xs shadow-2xl"
            >
              INGRESAR AL CLUB
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* NUEVO: Overlay Fullscreen de Login/Reservas */}
      <AnimatePresence>
        {showLogin && <PilotosLogin onClose={() => setShowLogin(false)} />}
      </AnimatePresence>

      {/* Sections Flow */}
      <div className="relative z-10">
        <ScrollPortal>
          <Hero />
        </ScrollPortal>
        <Suspense fallback={<div className="h-screen w-full flex items-center justify-center text-cyan-400 font-racing animate-pulse tracking-widest text-xs">CARGANDO SECTORES...</div>}>
          <HowItWorks />
          <BentoGrid />
          <Paddock />
          <Leaderboard />
          <MapHUD />
          <Footer />
        </Suspense>
      </div>

      {/* Floating Action Buttons */}
      <AnimatePresence>
        {showBackToTop && (
          <div className="fixed bottom-6 right-6 z-[90] flex flex-col gap-4 items-end">
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              onClick={() => setShowLogin(true)}
              className="px-6 py-3 bg-white text-black font-racing font-bold uppercase text-[10px] tracking-widest italic shadow-[0_0_20px_rgba(255,255,255,0.4)] hover:bg-silver-200 transition-colors flex items-center gap-3 group"
            >
              <span>Reservar_Cupo</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </motion.button>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="w-12 h-12 bg-black/80 border border-cyan-400/50 text-cyan-400 flex items-center justify-center rounded-full backdrop-blur-md hover:bg-cyan-400 hover:text-black transition-colors shadow-[0_0_15px_rgba(0,243,255,0.2)] hover:shadow-[0_0_25px_rgba(0,243,255,0.5)]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </motion.button>
          </div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default App;
